'use client';

import { AtomicContext } from '@contexts/coveo/AtomicContext';
import { buildPager } from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import c from 'classnames';
import { CaretLeft, CaretRight } from 'phosphor-icons';
import { useContext, useEffect, useState } from 'react';

const commonButtonClasses =
  'size-10 mx-1 rounded-lg cursor-pointer border text-grey-600 border-grey-300 text-base hover:bg-grey-100 flex items-center justify-center';
const arrowButtonStyles = c(
  commonButtonClasses,
  'disabled:bg-grey-300 disabled:cursor-not-allowed disabled:text-grey-400'
);
const pageButtonStyles = c(
  commonButtonClasses,
  'font-bold disabled:bg-blue disabled:border-blue disabled:text-white disabled:cursor-not-allowed'
);

type Props = {
  paginationMicrocopySet: MicrocopyFragment[];
  context: AtomicContext;
};

export const HeadlessPager = ({
  paginationMicrocopySet = [],
  context,
}: Props) => {
  const { searchEngine } = useContext(context);
  const [pagerController] = useState(searchEngine && buildPager(searchEngine));
  const [pagerState, setPagerState] = useState(pagerController?.state);

  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
    const firstProductCardElement = document.querySelector(
      'atomic-layout-section[section="results"] article:first-child a'
    ) as HTMLElement;
    firstProductCardElement?.focus();
  };

  const previousPage = () => {
    pagerController?.previousPage();
    scrollToTop();
  };

  const selectPage = (page: number) => {
    pagerController?.selectPage(page);
    scrollToTop();
  };

  const nextPage = () => {
    pagerController?.nextPage();
    scrollToTop();
  };

  const {
    paginationMicrocopy,
    previousMicrocopy,
    pageMicrocopy,
    nextMicrocopy,
  } = getMicrocopyValues(paginationMicrocopySet);

  useEffect(
    () =>
      pagerController?.subscribe(() => setPagerState(pagerController?.state)),
    [pagerController]
  );

  return (
    <nav data-test="pager" aria-label={paginationMicrocopy} className="flex">
      <button
        aria-label={previousMicrocopy}
        className={arrowButtonStyles}
        role="link"
        disabled={!pagerState?.hasPreviousPage}
        onClick={previousPage}
        aria-hidden={!pagerState?.hasPreviousPage}
      >
        <CaretLeft size={20} />
      </button>
      {pagerState?.currentPages.map((page) => (
        <button
          aria-label={`${pageMicrocopy} ${page}`}
          className={pageButtonStyles}
          key={page}
          role="link"
          aria-current={pagerController?.isCurrentPage(page) ? true : false}
          disabled={pagerController?.isCurrentPage(page)}
          onClick={() => selectPage(page)}
        >
          {page}
        </button>
      ))}
      <button
        aria-label={nextMicrocopy}
        role="link"
        className={arrowButtonStyles}
        onClick={nextPage}
        disabled={!pagerState?.hasNextPage}
        aria-hidden={!pagerState?.hasNextPage}
      >
        <CaretRight size={20} />
      </button>
    </nav>
  );
};

const getMicrocopyValues = (microcopySet?: MicrocopyFragment[]) => {
  const paginationMicrocopy = microcopySet?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'pagination.pagination-aria-label'
  )?.value;
  const previousMicrocopy = microcopySet?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'pagination.previous-aria-label'
  )?.value;
  const pageMicrocopy = microcopySet?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'pagination.page-aria-label'
  )?.value;
  const nextMicrocopy = microcopySet?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'pagination.next-aria-label'
  )?.value;

  return {
    paginationMicrocopy: paginationMicrocopy ?? 'Pagination',
    previousMicrocopy: previousMicrocopy ?? 'Previous',
    pageMicrocopy: pageMicrocopy ?? 'Page',
    nextMicrocopy: nextMicrocopy ?? 'Next',
  };
};

export default HeadlessPager;
